<template>
  <div class="edu-application-modal modal__wrap--bg cmp-modal is-active" :id="modalId" style="display: flex;">
    <div class="modal-wrap-container modal-wd1020">
      <!-- 모달 헤더 -->
      <div class="modal-header">
        <h2 class="title" tabindex="-1">수강 신청</h2>
      </div>

      <!-- 모달 바디 -->
      <div class="modal-body-container ht-full">
        <!-- 진행 단계 표시 -->
                      <div class="gray-box yakguan-step">
                        <ol>
                          <li :class="{'is-active': currentStep === 1}" :aria-current="currentStep === 1 ? 'step' : null">
                            <span>1</span> 약관<br class="mo-only">동의<span v-if="currentStep === 1" class="sr-only"> 현재단계</span>
                          </li>
                          <li :class="{'is-active': currentStep === 2}" :aria-current="currentStep === 2 ? 'step' : null">
                            <span>2</span> 기본정보<br class="mo-only">확인<span v-if="currentStep === 2" class="sr-only"> 현재단계</span>
                          </li>
                          <li :class="{'is-active': currentStep === 3}" :aria-current="currentStep === 3 ? 'step' : null">
                            <span>3</span> 추가<br class="mo-only">입력 사항<span v-if="currentStep === 3" class="sr-only"> 현재단계</span>
                          </li>
                        </ol>
                      </div>

        <!-- 단계별 컴포넌트 동적 렌더링 -->
        <component
            :is="currentStepComponent"
            :lecture-id="lectureId"
            :lecture-info="lectureInfo"
            :step-data="stepData[currentStep]"
            :org-list="orgList"
            :member-info="currentStep === 3 ? buildMemberInfo(stepData[2]) : null"
            :discount-info="currentStep === 3 ? stepData[1] : null"
            @step-validated="handleStepValidation"
            @update-data="handleDataUpdate"
            @next-step="goToNextStep"
            @prev-step="goToPrevStep"
            @application-success="handleApplicationSuccess"
            @application-error="handleApplicationError"
        ></component>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <!-- 2단계 이후: 이전으로 버튼 -->
        <button
            type="button"
            class="btn btn-secondary btn-md button-wid150"
            @click="goToPrevStep"
            :disabled="currentStep < 3"
            v-if="currentStep > 2"
        >
          이전으로
        </button>

        <!-- 1단계: 동의하지 않음 버튼 -->
        <button
            v-if="currentStep === 1"
            type="button"
            class="btn btn-secondary btn-md button-wid150"
            @click="closeModal"
        >
          동의하지 않음
        </button>

        <!-- 주 액션 버튼 -->
        <button
            type="button"
            class="btn btn-primary btn-md button-wid150"
            @click="handleFooterButtonClick"
        >
          {{ footerButtonText }}
        </button>
      </div>

      <!-- 모달 닫기 버튼 -->
      <button type="button" class="btn btn-close only-icon close-modal" aria-label="닫기" @click="closeModal" title="닫기">
        <i class="ico ico-x-bl"></i>
      </button>
    </div>
  </div>
</template>

<script>
// 동적 컴포넌트 import (지연 로딩)
const EduStep1Agreement = () => import('./components/EduStep1Agreement.vue');
const EduStep2BasicInfo = () => import('./components/EduStep2BasicInfo.vue');
const EduStep3AdditionalForm = () => import('./components/EduStep3AdditionalForm.vue');

export default {
  name: 'EduApplicationModal',

  components: {
    EduStep1Agreement,
    EduStep2BasicInfo,
    EduStep3AdditionalForm,
  },

  props: {
    lectureId: {
      type: [String, Number],
      required: true,
    },
    lectureType: {
      type: String,
      default: 'FREE',
      validator: value => ['FREE', 'FREE_CONFIRM', 'CHARGED', 'CHARGED_CONFIRM'].includes(value),
    },
    modalId: {
      type: String,
      default: 'eduApplicationModal',
    },
    orgList: {
      type: Array,
      default: () => [],
      required: false,
    },
    lectureInfo: {
      type: Object,
      default: () => ({
        isCampus: false,
        isFree: false,
      }),
      required: false,
    },
  },

  data() {
    return {
      currentStep: 1,
      canProceed: false,
      isSubmitting: false,

      // 단계별 데이터 저장
      stepData: {
        1: {
          agreements: [],
          checkedAgreements: [],
          discountType: 'NONE',
          discountTypeLabel: '해당사항 없음',
          isValid: false,
        },
        2: {
          contactInfo: {},
          addressInfo: {},
          interests: [],
          isValid: false,
        },
        3: {
          motive: '',
          dynamicForms: [],
          formAnswers: {},
          isValid: false,
        },
      },

      // 컴포넌트 매핑
      stepComponents: {
        1: 'EduStep1Agreement',
        2: 'EduStep2BasicInfo',
        3: 'EduStep3AdditionalForm',
      },
    };
  },

  computed: {
    currentStepComponent() {
      return this.stepComponents[this.currentStep];
    },

    footerButtonText() {
      if (this.currentStep === 1) {
        return '동의';
      } else if (this.currentStep === 3) {
        return this.isSubmitting ? '신청 중...' : '신청하기';
      }
      return '다음단계';
    },
  },

  methods: {
    /**
     * 단계별 검증 상태 처리
     */
    handleStepValidation(isValid) {
      this.canProceed = isValid;
      this.stepData[this.currentStep].isValid = isValid;
    },

    /**
     * 단계별 데이터 업데이트 처리
     */
    handleDataUpdate(data) {
      this.stepData[this.currentStep] = {
        ...this.stepData[this.currentStep],
        ...data,
      };
    },

    /**
     * 다음 단계로 이동
     */
    goToNextStep() {
      // 현재 단계 컴포넌트에서 직접 검증 수행
      const currentStepComponent = this.$children.find(child =>
          child.$options.name === this.stepComponents[this.currentStep],
      );

      if (currentStepComponent && typeof currentStepComponent.validateStep === 'function') {
        currentStepComponent.validateStep(true); // 사용자가 버튼을 클릭한 경우이므로 alert 메시지 표시

        // 검증 결과 대기 (validateStep이 emit을 통해 결과를 전달함)
        this.$nextTick(() => {
          if (!this.stepData[this.currentStep].isValid) {
            // 검증 실패 - 각 단계별 개별 메시지 처리
            return;
          }

          if (this.currentStep < 3) {
            this.currentStep += 1;
            this.canProceed = this.stepData[this.currentStep].isValid || false;
            this.scrollToTop();
          }
        });
      } else {
        // 기존 로직 fallback
        if (!this.stepData[this.currentStep].isValid) {
          // 각 단계별 개별 메시지 처리
          return;
        }

        if (this.currentStep < 3) {
          this.currentStep += 1;
          this.canProceed = this.stepData[this.currentStep].isValid || false;
          this.scrollToTop();
        }
      }
    },

    /**
     * 이전 단계로 이동
     */
    goToPrevStep() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
        this.canProceed = this.stepData[this.currentStep].isValid || false;
        this.scrollToTop();
      }
    },

    /**
     * 푸터 버튼 클릭 처리
     */
    handleFooterButtonClick() {
      if (this.currentStep === 3) {
        // 3단계에서는 자식 컴포넌트의 submitApplication() 메서드를 직접 호출
        const currentStepComponent = this.$children.find(child =>
            child.$options.name === this.stepComponents[this.currentStep],
        );

        if (currentStepComponent && typeof currentStepComponent.submitApplication === 'function') {
          currentStepComponent.submitApplication();
        } else {
          alert('수강 신청을 처리할 수 없습니다.');
        }
      } else {
        this.goToNextStep();
      }
    },

    /**
     * 최종 수강 신청 처리
     */
    submitApplication() {
      if (!this.canProceed || this.isSubmitting) {
        return Promise.resolve();
      }

      this.isSubmitting = true;
      const payload = this.buildApplicationPayload();

      return fetch(`/api/lectures/${this.lectureId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      })
          .then((response) => {
            if (response.ok) {
              return response.json().then((result) => {
                this.handleSubmissionSuccess(result);
                return result;
              });
            }
            // 에러 응답도 JSON으로 파싱하여 서버 메시지 추출
            return response.json().then((errorData) => {
              const errorMessage = errorData.msg || errorData.message || '수강 신청 중 오류가 발생했습니다.';
              this.handleSubmissionError(errorMessage);
              throw new Error(errorMessage);
            }).catch(() => {
              // JSON 파싱 실패시 기본 메시지
              const defaultMessage = `수강 신청 실패 (HTTP ${response.status})`;
              this.handleSubmissionError(defaultMessage);
              throw new Error(defaultMessage);
            });
          })
          .catch((error) => {
            this.handleSubmissionError(error.message || error);
            throw error;
          })
          .finally(() => {
            this.isSubmitting = false;
          });
    },

    /**
     * 신청 데이터 페이로드 생성 - CourseApplicationDto + MemberDto 통합
     */
    buildApplicationPayload() {
      const step1Data = this.stepData[1];
      const step2Data = this.stepData[2];
      const step3Data = this.stepData[3];

      console.log('전체 stepData:', this.stepData);
      console.log('step1Data:', step1Data);
      console.log('step1Data.discountType:', step1Data.discountType);
      console.log('step2Data:', step2Data);

      const payload = {
        motive: step3Data.motive || '',
        forms: Object.entries(step3Data.formAnswers || {}).map(([seq, answer]) => ({
          seq: parseInt(seq, 10),
          question: (step3Data.dynamicForms && step3Data.dynamicForms.find(f => f.seq === parseInt(seq, 10)) && step3Data.dynamicForms.find(f => f.seq === parseInt(seq, 10)).question) || '',
          answer: JSON.stringify(answer),
        })),
        discount: step1Data && step1Data.discountType !== 'NONE' 
          ? step1Data.discountType 
          : '',
      };

      // 2단계 회원 정보가 있으면 추가
      if (step2Data && step2Data.formData) {
        console.log('2단계 데이터 있음, memberInfo 생성 시도');
        const memberInfo = this.buildMemberInfo(step2Data);
        if (memberInfo) {
          payload.memberInfo = memberInfo;
          console.log('memberInfo 추가됨:', memberInfo);
        } else {
          console.log('memberInfo가 null로 반환됨');
        }
      } else {
        console.log('step2Data 또는 step2Data.formData가 없음');
      }

      console.log('최종 payload:', payload);
      return payload;
    },

    /**
     * 회원 정보 데이터 생성
     */
    buildMemberInfo(step2Data) {
      console.log('step2Data:', step2Data);

      const formData = step2Data.formData;
      const contactInfo = step2Data.contactInfo;
      const addressInfo = step2Data.addressInfo;

      console.log('formData:', formData);
      console.log('contactInfo:', contactInfo);
      console.log('addressInfo:', addressInfo);

      if (!formData && !contactInfo && !addressInfo) {
        console.log('formData, contactInfo, addressInfo가 모두 없음');
        return null;
      }

      const memberInfo = {};

      // contactInfo에서 가져오는 정보들
      if (contactInfo) {
        // 주된 직업군 정보
        if (contactInfo.occupationStr) {
          memberInfo.occuPation = contactInfo.occupationStr;
          console.log('직업군 설정:', contactInfo.occupationStr);
        } else {
          memberInfo.occuPation = '기타';
          memberInfo.occuPationEtc = '';
          console.log('직업군 설정없음 :', contactInfo.occupationStr);
        }

        // 성별 정보
        if (contactInfo.gender) {
          memberInfo.gender = contactInfo.gender;
          console.log('성별 설정:', contactInfo.gender);
        }

        // 연락처 정보
        if (contactInfo.phone1 && contactInfo.phone2 && contactInfo.phone3) {
          memberInfo.phone1 = contactInfo.phone1;
          memberInfo.phone2 = contactInfo.phone2;
          memberInfo.phone3 = contactInfo.phone3;
          console.log('연락처 설정:', `${contactInfo.phone1}-${contactInfo.phone2}-${contactInfo.phone3}`);
        }

        // 이메일 정보
        if (contactInfo.email) {
          memberInfo.email = contactInfo.email;
          console.log('이메일 설정:', contactInfo.email);
        }

        // 가입경로
        if (contactInfo.applyPath) {
          memberInfo.applyPath = contactInfo.applyPath;
          console.log('가입경로 설정:', contactInfo.applyPath);
        }

        // 가입동기
        if (contactInfo.joinPurpose) {
          memberInfo.joinPurpose = contactInfo.joinPurpose;
          console.log('가입동기 설정:', contactInfo.joinPurpose);
        }

        // 관심기관
        if (contactInfo.likeOrgs && Array.isArray(contactInfo.likeOrgs)) {
          memberInfo.likeOrgs = contactInfo.likeOrgs;
          console.log('관심기관 설정:', contactInfo.likeOrgs);
        }

        // 이메일/SMS 수신 여부
        if (contactInfo.emailReceive) {
          memberInfo.emailCheck = contactInfo.emailReceive;
          console.log('이메일 수신 설정:', contactInfo.emailReceive);
        }

        if (contactInfo.smsReceive) {
          memberInfo.smsCheck = contactInfo.smsReceive;
          console.log('SMS 수신 설정:', contactInfo.smsReceive);
        }

        // 사진 URL
        if (contactInfo.photoUrl) {
          memberInfo.photoUrl = contactInfo.photoUrl;
          console.log('사진 URL 설정:', contactInfo.photoUrl);
        }
      }

      // addressInfo에서 주소 정보 가져오기
      if (addressInfo) {
        if (addressInfo.zipcode) {
          memberInfo.zipcode = addressInfo.zipcode;
          console.log('우편번호 설정:', addressInfo.zipcode);
        }
        if (addressInfo.street) {
          memberInfo.addr1 = addressInfo.street;
          console.log('기본주소 설정:', addressInfo.street);
        }
        if (addressInfo.detail) {
          memberInfo.addr2 = addressInfo.detail;
          console.log('상세주소 설정:', addressInfo.detail);
        }
      }

      // formData에서 가져오는 정보들 (fallback)
      if (formData) {
        if (!memberInfo.gender && formData.gender) {
          memberInfo.gender = formData.gender;
          console.log('formData에서 성별 설정:', formData.gender);
        }

        // formData에서 주소 정보 fallback
        if (!memberInfo.zipcode && formData.zipcode) {
          memberInfo.zipcode = formData.zipcode;
          console.log('formData에서 우편번호 설정:', formData.zipcode);
        }
        if (!memberInfo.addr1 && formData.addr1) {
          memberInfo.addr1 = formData.addr1;
          console.log('formData에서 기본주소 설정:', formData.addr1);
        }
        if (!memberInfo.addr2 && formData.addr2) {
          memberInfo.addr2 = formData.addr2;
          console.log('formData에서 상세주소 설정:', formData.addr2);
        }

        // formData에서 연락처 정보 fallback
        if (!memberInfo.phone1 && formData.phone1) {
          memberInfo.phone1 = formData.phone1;
          memberInfo.phone2 = formData.phone2 || '';
          memberInfo.phone3 = formData.phone3 || '';
          console.log('formData에서 연락처 설정:', `${memberInfo.phone1}-${memberInfo.phone2}-${memberInfo.phone3}`);
        }
        if (!memberInfo.email && formData.email) {
          memberInfo.email = formData.email;
          console.log('formData에서 이메일 설정:', formData.email);
        }

        // formData에서 추가 필드들 fallback
        if (!memberInfo.applyPath && formData.applyPath) {
          memberInfo.applyPath = formData.applyPath;
          console.log('formData에서 가입경로 설정:', formData.applyPath);
        }

        if (!memberInfo.joinPurpose && formData.joinPurpose) {
          memberInfo.joinPurpose = formData.joinPurpose;
          console.log('formData에서 가입동기 설정:', formData.joinPurpose);
        }

        if (!memberInfo.likeOrgs && formData.likeOrgs && Array.isArray(formData.likeOrgs)) {
          memberInfo.likeOrgs = formData.likeOrgs;
          console.log('formData에서 관심기관 설정:', formData.likeOrgs);
        }

        // formData에서 이메일/SMS 수신 여부 fallback
        if (!memberInfo.emailCheck && formData.emailReceive) {
          memberInfo.emailCheck = formData.emailReceive;
          console.log('formData에서 이메일 수신 설정:', formData.emailReceive);
        }

        if (!memberInfo.smsCheck && formData.smsReceive) {
          memberInfo.smsCheck = formData.smsReceive;
          console.log('formData에서 SMS 수신 설정:', formData.smsReceive);
        }
      }

      console.log('최종 memberInfo:', memberInfo);

      // 빈 객체가 아닌 경우에만 반환
      return Object.keys(memberInfo).length > 0 ? memberInfo : null;
    },

    /**
     * 신청 성공 처리
     */
    handleSubmissionSuccess(result) {
      if (result && typeof result === 'number') {
        // 결과가 숫자(courseId)인 경우 결제 페이지로 리다이렉트
        alert('수강 신청이 완료되었습니다. 결제 페이지로 이동합니다.');
        if (typeof window !== 'undefined') {
          window.location.href = `/education-pay.do?id=${result}`;
        }
      } else {
        // 기본 처리 방식 유지
        alert('수강 신청이 완료되었습니다.');
        this.closeModal();

        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
    },

    /**
     * 신청 실패 처리
     */
    handleSubmissionError(error) {
      // CourseFormJobType.vue와 동일하게 서버에서 전달한 에러 메시지 표시
      const errorMessage = typeof error === 'string' ? error : (error.message || '수강 신청 중 오류가 발생했습니다.');
      alert(errorMessage);
    },


    /**
     * 모달 닫기
     */
    closeModal() {
      // 기존 JB 패턴의 모달 닫기 함수 호출
      if (typeof window.closeEduApplicationModal === 'function') {
        window.closeEduApplicationModal();
      } else if (typeof window !== 'undefined' && window.closeModal) {
        window.closeModal(event);
      } else {
        // 폴백: 모달 숨기기
        this.$el.style.display = 'none';
      }
    },

    /**
     * 모달 스크롤을 상단으로 이동 + 포커스를 모달 상단으로 이동
     */
    scrollToTop() {
      this.$nextTick(() => {
        const modalBody = this.$el.querySelector('.modal-body-container');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
        // 단계 전환 시 포커스를 모달 상단(h2.title)으로 이동
        // → 스크린리더가 "수강 신청" 읽은 후 새 단계 내용을 순서대로 탐색 가능
        requestAnimationFrame(() => {
          const titleEl = this.$el.querySelector('h2.title[tabindex="-1"]');
          if (titleEl) {
            titleEl.focus();
          }
        });
      });
    },

    /**
     * 컴포넌트 초기화
     */
    initializeModal() {
      // 첫 번째 단계 활성화
      this.currentStep = 1;
      this.canProceed = false;
    },

    /**
     * 수강 신청 성공 이벤트 처리
     */
    handleApplicationSuccess(result) {
      // 모달 초기화
      this.initializeModal();

      // 단계별 데이터 초기화
      this.stepData = {
        1: { agreed: false, discountType: 'NONE', discountTypeLabel: '' },
        2: { validated: false },
        3: { motive: '', dynamicForms: [], formAnswers: {}, isValid: false }
      };

      alert("신청이 완료되었습니다. 신청정보는 마이페이지>수강신청내역>내강좌 화면에서 확인 할 수 있습니다.");
      this.closeModal();

      // 부모 컴포넌트에 성공 이벤트 전파
      this.$emit('application-completed', { ...result, lectureType: this.lectureType });

      // 강좌 타입에 따른 분기 처리
      // 유료 선착순 강좌인 경우 결제 페이지로 이동
      if (this.lectureType === 'CHARGED' && result && result.courseId) {
        setTimeout(() => {
          if (typeof window !== 'undefined') {
            window.location.href = `/mypage-education-pay.do?id=${result.courseId}`;
          }
        }, 500);
        return;
      }

      // 페이지 새로고침으로 버튼 상태 업데이트
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }, 500);
    },

    /**
     * 수강 신청 실패 이벤트 처리
     */
    handleApplicationError(error) {
      // 오류 메시지는 자식 컴포넌트에서 이미 표시했으므로 추가 처리는 하지 않음
      // 필요시 부모 컴포넌트에 실패 이벤트 전파
      this.$emit('application-failed', error);
    },

    /**
     * 레이어팝업 포커스 트랩 (웹접근성 2.1.2 키보드 함정 없음 / 2.4.3 초점 순서)
     * - Tab/Shift+Tab이 모달 밖으로 나가지 않도록 처음↔끝을 순환
     * - ESC 닫기는 통합 스크립트(EduApplicationModalIntegration.js)가 처리
     */
    trapFocus(e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) {
        return;
      }
      const modal = this.$el;
      // 모달이 보이지 않으면(닫힘 등) 트랩하지 않음
      if (!modal || !(modal.offsetWidth || modal.offsetHeight || modal.getClientRects().length)) {
        return;
      }
      const focusable = this.getFocusableElements(modal);
      if (!focusable.length) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey) {
        if (active === first || !modal.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !modal.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    },

    /**
     * 모달 내에서 실제로 보이는 포커스 가능한 요소 목록
     */
    getFocusableElements(modal) {
      const SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      return Array.prototype.filter.call(modal.querySelectorAll(SELECTOR), (el) => {
        const visible = !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
        return visible && !el.closest('[aria-hidden="true"]');
      });
    },
  },

  mounted() {
    this.initializeModal();
    // 포커스 트랩 등록 (캡처 단계에서 가로채 다른 핸들러보다 우선)
    this._trapFocusHandler = this.trapFocus.bind(this);
    document.addEventListener('keydown', this._trapFocusHandler, true);
  },

  beforeDestroy() {
    // 모달 닫힘($destroy) 시 포커스 트랩 리스너 제거 (누수 방지)
    if (this._trapFocusHandler) {
      document.removeEventListener('keydown', this._trapFocusHandler, true);
      this._trapFocusHandler = null;
    }
  },
};
</script>

<style scoped>
/* Vue 컴포넌트 전용 스타일 - 기존 CSS와 충돌 방지 */
/**
 * 단계 표시
  * - 색만 구분 방지: 굵기 + 활성 뱃지 외곽선 유지
 */
.edu-application-modal .gray-box.yakguan-step {
  --edu-step-active-blue: #006eda;
  --edu-step-inactive-label: #586d8c;
  --edu-step-inactive-num: #3d5280;
}

.edu-application-modal .gray-box.yakguan-step ol li:not(.is-active) {
  font-weight: 500;
  color: var(--edu-step-inactive-label);
}

.edu-application-modal .gray-box.yakguan-step ol li.is-active {
  font-weight: 800;
  color: var(--edu-step-active-blue);
}

.edu-application-modal .gray-box.yakguan-step ol li:not(.is-active) span {
  font-weight: 600;
  color: var(--edu-step-inactive-num);
}

/* sub.css line-height(23/20px)가 flex 정렬과 맞지 않아 숫자가 치우침 — 높이 안에서는 line-height:1 + flex만 사용 */
.edu-application-modal .gray-box.yakguan-step ol li span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
  box-sizing: border-box;
}

.edu-application-modal .gray-box.yakguan-step ol li.is-active span {
  background-color: var(--edu-step-active-blue);
  color: #fff;
  /* 채도와 무관하게 현재 단계 뱃지 구분 */
  box-shadow: 0 0 0 2px #fff, 0 0 0 4px #1a1a1a;
}

/* 모달 애니메이션 최적화 */
.modal-body-container {
  transition: all 0.3s ease;
}

/* 버튼 상태별 스타일 */
/* 비활성 버튼: opacity(반투명)는 명도대비 저하 → 단색 사용 (WCAG 1.4.3) */
.btn:disabled {
  cursor: not-allowed;
}
.btn-primary:disabled {
  background-color: #6F6F6F; /* 흰 글자 대비 4.78:1 (AA 충족) */
  color: #fff;
}
.btn-secondary:disabled {
  background-color: #ededed;
  color: #666666; /* 대비 4.90:1 */
  border-color: #d9d9d9;
}

/* 로딩 상태 표시 */
.btn.loading {
  position: relative;
}

.btn.loading::after {
  content: '';
  position: absolute;
  width: 16px;
  height: 16px;
  margin: auto;
  border: 2px solid transparent;
  border-top-color: #fff;
  border-radius: 50%;
  animation: loading 1s linear infinite;
  top: 0;
  bottom: 0;
  right: 10px;
}

@keyframes loading {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>

<!--
  비-scoped 전역 블록: 자식 step 컴포넌트(EduStep1/2/3)까지 적용되도록 .edu-application-modal 하위로 한정.
  scoped는 자식 컴포넌트 내부 요소를 못 잡으므로 의도적으로 비-scoped 사용 (수강신청 팝업 내부로만 영향)
-->
<style>
/* ── 수강신청 레이어팝업(약관동의·기본정보확인·추가입력) 3단계 공통 ── */
/* 인풋 테두리 진하게: gray-300(#D8D9DA, 약 1.4:1) → #767676 */
.edu-application-modal .input-area input[type="text"],
.edu-application-modal .input-area input[type="number"],
.edu-application-modal .input-area input[type="email"],
.edu-application-modal .input-area input[type="password"],
.edu-application-modal .input-area select,
.edu-application-modal .input-area textarea,
.edu-application-modal textarea {
  border-color: #767676;
}

/* 체크박스 유령체크 제거: 미체크 체크박스는 체크 이미지 없이 깨끗한 빈 박스로 강제 */
/* (체크 시 정상 표시 유지, 라디오는 제외 — input[type="checkbox"]만 대상) */
.edu-application-modal .check-wrap input[type="checkbox"]:not(:checked) + label::before {
  background-image: none;
  background-color: #fff;
}
</style>


// WEBPACK FOOTER //
// EduApplicationModal.vue?8e7348a2